<template>
  <transition name="spotlight-fade">
    <div v-if="visible" class="spotlight-tour" ref="root">
      <template v-if="!currentStep.center && holeReady">
        <div class="spotlight-panel top" :style="panelTop" />
        <div class="spotlight-panel left" :style="panelLeft" />
        <div class="spotlight-panel right" :style="panelRight" />
        <div class="spotlight-panel bottom" :style="panelBottom" />
        <div class="spotlight-hole" :style="holeStyle">
          <span class="spotlight-ring" />
        </div>
      </template>
      <div v-else class="spotlight-full-mask" />

      <div
        class="spotlight-card"
        :class="{ 'is-center': currentStep.center }"
        :style="cardStyle"
      >
        <div class="spotlight-step-badge" v-if="stepBadge">{{ stepBadge }}</div>
        <h3 class="spotlight-title">{{ currentStep.title }}</h3>
        <p class="spotlight-desc">{{ currentStep.desc }}</p>
        <p v-if="clickWaiting" class="spotlight-click-hint">
          {{ currentStep.clickHint || "请点击高亮区域继续" }}
        </p>
        <div class="spotlight-actions">
          <el-button size="small" type="text" @click="skip">{{ $tt("跳过引导") }}</el-button>
          <span class="spotlight-spacer" />
          <el-button v-if="stepIndex > 0" size="small" @click="prev">{{ $tt("上一步") }}</el-button>
          <el-button
            v-if="showNextButton"
            size="small"
            type="primary"
            @click="onNextClick"
          >
            {{ nextLabel }}
          </el-button>
        </div>
        <div class="spotlight-dots">
          <span
            v-for="(s, i) in steps"
            :key="s.id"
            class="dot"
            :class="{ active: i === stepIndex }"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import eventBus from "@/utils/eventBus";
import {
  onboardingSteps,
  markTourFinished,
  isTourFinished,
} from "@/constants/onboardingTour";

const RESIZE_EVENTS = ["resize", "scroll"];

export default {
  name: "SpotlightTour",
  data() {
    return {
      visible: false,
      steps: onboardingSteps,
      stepIndex: 0,
      hole: null,
      holeReady: false,
      clickWaiting: false,
      scrollParents: [],
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.stepIndex] || {};
    },
    stepBadge() {
      if (this.currentStep.center) {
        return this.currentStep.action === "finish" ? "完成" : "开始";
      }
      const n = this.stepIndex;
      if (n <= 0) return "";
      return `第 ${n} / ${this.steps.length - 2} 步`;
    },
    showNextButton() {
      return this.currentStep.action !== "click" || !this.clickWaiting;
    },
    nextLabel() {
      if (this.currentStep.action === "finish") return "开始使用";
      if (this.stepIndex >= this.steps.length - 1) return "完成";
      return "下一步";
    },
    holeStyle() {
      if (!this.hole) return {};
      return {
        top: `${this.hole.top}px`,
        left: `${this.hole.left}px`,
        width: `${this.hole.width}px`,
        height: `${this.hole.height}px`,
      };
    },
    panelTop() {
      if (!this.hole) return {};
      return { top: 0, left: 0, right: 0, height: `${Math.max(0, this.hole.top)}px` };
    },
    panelLeft() {
      if (!this.hole) return {};
      return {
        top: `${this.hole.top}px`,
        left: 0,
        width: `${Math.max(0, this.hole.left)}px`,
        height: `${this.hole.height}px`,
      };
    },
    panelRight() {
      if (!this.hole) return {};
      return {
        top: `${this.hole.top}px`,
        left: `${this.hole.left + this.hole.width}px`,
        right: 0,
        height: `${this.hole.height}px`,
      };
    },
    panelBottom() {
      if (!this.hole) return {};
      return {
        top: `${this.hole.top + this.hole.height}px`,
        left: 0,
        right: 0,
        bottom: 0,
      };
    },
    cardStyle() {
      if (this.currentStep.center || !this.hole) return {};
      const gap = 16;
      const cardW = 320;
      const cardH = 220;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const h = this.hole;
      let top = h.top + h.height + gap;
      let left = h.left;
      const placement = this.currentStep.placement || "bottom";

      if (placement === "top") {
        top = h.top - cardH - gap;
      } else if (placement === "left") {
        top = h.top + h.height / 2 - cardH / 2;
        left = h.left - cardW - gap;
      } else if (placement === "right") {
        top = h.top + h.height / 2 - cardH / 2;
        left = h.left + h.width + gap;
      } else if (placement === "bottom") {
        top = h.top + h.height + gap;
        left = h.left + h.width / 2 - cardW / 2;
      }

      top = Math.min(Math.max(12, top), vh - cardH - 12);
      left = Math.min(Math.max(12, left), vw - cardW - 12);
      return { top: `${top}px`, left: `${left}px`, width: `${cardW}px` };
    },
  },
  mounted() {
    eventBus.$on("startSpotlightTour", this.start);
    eventBus.$on("restartSpotlightTour", this.restart);
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    eventBus.$off("startSpotlightTour", this.start);
    eventBus.$off("restartSpotlightTour", this.restart);
    window.removeEventListener("keydown", this.onKeydown);
    this.teardownListeners();
  },
  methods: {
    start(options = {}) {
      if (options.force !== true && isTourFinished()) return;
      this.stepIndex = 0;
      this.visible = true;
      document.body.classList.add("spotlight-tour-open");
      this.$nextTick(() => this.applyStep());
    },
    restart() {
      this.start({ force: true });
    },
    skip() {
      markTourFinished();
      this.close();
    },
    close() {
      this.visible = false;
      this.clickWaiting = false;
      this.holeReady = false;
      document.body.classList.remove("spotlight-tour-open");
      this.teardownListeners();
    },
    prev() {
      if (this.stepIndex <= 0) return;
      this.stepIndex--;
      this.applyStep();
    },
    onNextClick() {
      if (this.currentStep.action === "finish") {
        markTourFinished();
        this.close();
        return;
      }
      this.next();
    },
    next() {
      if (this.stepIndex >= this.steps.length - 1) {
        markTourFinished();
        this.close();
        return;
      }
      this.stepIndex++;
      this.applyStep();
    },
    applyStep() {
      this.teardownListeners();
      this.clickWaiting = false;
      this.holeReady = false;
      this.hole = null;

      this.$nextTick(() => {
        if (this.currentStep.center) {
          this.holeReady = true;
          if (this.currentStep.action === "click") this.bindClickWait();
          return;
        }
        this.updateHole();
        if (!this.holeReady && this.currentStep.selector) {
          setTimeout(() => {
            this.updateHole();
            if (!this.holeReady) this.next();
          }, 400);
        }
        this.bindResize();
        if (this.currentStep.action === "click") {
          this.clickWaiting = true;
          this.bindClickWait();
        }
      });
    },
    updateHole() {
      const sel = this.currentStep.selector;
      if (!sel) {
        this.holeReady = true;
        return;
      }
      const el = document.querySelector(sel);
      if (!el) {
        this.holeReady = false;
        return;
      }
      try {
        el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      } catch (e) {
        /* ignore */
      }
      const rect = el.getBoundingClientRect();
      const pad = this.currentStep.padding != null ? this.currentStep.padding : 8;
      this.hole = {
        top: Math.max(0, rect.top - pad),
        left: Math.max(0, rect.left - pad),
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      };
      this.holeReady = true;
    },
    bindResize() {
      const handler = () => this.updateHole();
      RESIZE_EVENTS.forEach((ev) => window.addEventListener(ev, handler, true));
      this._resizeHandler = handler;
    },
    teardownListeners() {
      if (this._resizeHandler) {
        RESIZE_EVENTS.forEach((ev) =>
          window.removeEventListener(ev, this._resizeHandler, true)
        );
        this._resizeHandler = null;
      }
      if (this._clickHandler) {
        document.removeEventListener("click", this._clickHandler, true);
        this._clickHandler = null;
      }
    },
    bindClickWait() {
      const sel = this.currentStep.selector;
      const handler = (e) => {
        if (this.currentStep.action !== "click") return;
        if (this.currentStep.center) {
          this.next();
          return;
        }
        const el = document.querySelector(sel);
        if (!el) return;
        if (el.contains(e.target) || el === e.target) {
          e.stopPropagation();
          this.clickWaiting = false;
          setTimeout(() => this.next(), 280);
        }
      };
      this._clickHandler = handler;
      setTimeout(() => {
        document.addEventListener("click", handler, true);
      }, 120);
    },
    onKeydown(e) {
      if (!this.visible) return;
      if (e.key === "Escape") this.skip();
    },
  },
};
</script>

<style scoped lang="scss">
.spotlight-tour {
  position: fixed;
  inset: 0;
  z-index: 10050;
  pointer-events: none;
}

.spotlight-full-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
  pointer-events: auto;
}

.spotlight-panel {
  position: fixed;
  background: rgba(0, 0, 0, 0.62);
  pointer-events: auto;
  transition: all 0.25s ease;
}

.spotlight-hole {
  position: fixed;
  pointer-events: none;
  z-index: 1;
  border-radius: 8px;
  transition: all 0.25s ease;
}

.spotlight-ring {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 0 0 2px #409eff, 0 0 16px rgba(64, 158, 255, 0.55);
  animation: spotlight-pulse 1.4s ease-in-out infinite;
}

.spotlight-card {
  position: fixed;
  z-index: 2;
  width: 320px;
  padding: 20px 20px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  text-align: left;
  pointer-events: auto;
  transition: top 0.25s ease, left 0.25s ease;

  &.is-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.spotlight-step-badge {
  display: inline-block;
  padding: 2px 10px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #409eff;
  background: #ecf5ff;
  border-radius: 12px;
}

.spotlight-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

.spotlight-desc {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
}

.spotlight-click-hint {
  margin: 0 0 12px;
  padding: 8px 10px;
  font-size: 13px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 6px;
}

.spotlight-actions {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.spotlight-spacer {
  flex: 1;
}

.spotlight-dots {
  display: flex;
  justify-content: center;
  gap: 6px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #dcdfe6;
    transition: background 0.2s;

    &.active {
      background: #409eff;
      transform: scale(1.15);
    }
  }
}

.spotlight-fade-enter-active,
.spotlight-fade-leave-active {
  transition: opacity 0.25s;
}

.spotlight-fade-enter,
.spotlight-fade-leave-to {
  opacity: 0;
}

@keyframes spotlight-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 2px #409eff, 0 0 12px rgba(64, 158, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 3px #66b1ff, 0 0 22px rgba(64, 158, 255, 0.75);
  }
}
</style>

<style lang="scss">
body.spotlight-tour-open {
  overflow: hidden;
}
</style>
